import React, { useState, useEffect, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { loadStripeForUser, loadStripeWithRetry } from '../utils/stripe-loader';
import authService from '../services/auth-service';
import PaymentModal from './PaymentModal';
import { isStripeTestUser } from '../utils/stripe-config';
import StripeErrorBoundary from './StripeErrorBoundary';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (user: any) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLogin }) => {
  const [activeTab, setActiveTab] = useState<'sales' | 'login' | 'register' | 'payment'>('sales');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [wantsSupportersClub, setWantsSupportersClub] = useState(false);

  // Dynamic Stripe promise: test users get sandbox key even in production
  const stripePromise = useMemo(() => {
    if (email && isStripeTestUser(email)) {
      console.log('STRIPE SANDBOX: Loading test Stripe for user ' + email);
      return loadStripeForUser(email);
    }
    return loadStripeWithRetry();
  }, [email]);

  useEffect(() => {
    setWantsSupportersClub(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const authResponse = await authService.login({ email, password });
      onLogin(authResponse.user);
      onClose();
    } catch (error) {
      // Show the professional error message from the backend
      setError(error instanceof Error ? error.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authService.register({ email, password });
      
      if (wantsSupportersClub) {
        // Go to payment after successful registration
        setActiveTab('payment');
      } else {
        // Show success message - user needs to verify email
        setError(''); // Clear any errors
        // Don't auto-login, user needs to verify email first
        onClose(); // Close modal and let them check email
      }
    } catch (error) {
      // Show the professional error message from the backend
      setError(error instanceof Error ? error.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (email: string) => {
    // After successful payment, complete the registration
    setEmail(email);
    // The account was already created, so we can just close the modal
    // The user will be logged in automatically after payment
    onClose();
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      // Google OAuth will redirect to Google's OAuth page
      // We don't wait for a response as the page will redirect
      authService.googleLogin();
      // Note: The function will redirect, so this code won't execute
    } catch (error) {
      // This should only happen if there's an actual error before redirect
      setError('Google login failed. Please try again.');
      setLoading(false);
    }
    // Don't set loading to false here as the page will redirect
  };

  const handleGoogleRegister = async () => {
    setLoading(true);
    setError('');

    try {
      // Google OAuth will redirect to Google's OAuth page
      // We don't wait for a response as the page will redirect
      authService.googleLogin();
      // Note: The function will redirect, so this code won't execute
    } catch (error) {
      // This should only happen if there's an actual error before redirect
      setError('Google registration failed. Please try again.');
      setLoading(false);
    }
    // Don't set loading to false here as the page will redirect
  };

  const renderSalesPage = () => (
    <div className="sales-page">
      <div className="sales-header">
        <h1>Larry's Help</h1>
        <p className="sales-subtitle">Easy to use financial planning tools for everyone.</p>
      </div>
      <div className="sales-signin">
      <div className="sales-google-signin">          
        <button 
          onClick={handleGoogleRegister} 
          className="google-signin-button sales-google-button"
          disabled={loading}
        >
          {loading ? (
            <span className="loading-spinner">⏳</span>
          ) : (
            <>
              <img 
                src="https://developers.google.com/identity/images/g-logo.png" 
                alt="Google" 
                className="google-icon"
              />
              Continue with Google
            </>
          )}
        </button>
      </div>


        <div>Already have an account? <button onClick={() => setActiveTab('login')} className="link-button">Sign In Here</button></div>
      </div>

      <div className="tier-comparison">
        <div className="tier-card guest-tier">
          <div className="tier-header">
            <h3>Guest Mode</h3>
          </div>
          <div className="tier-features">
            <div className="feature">
              <span>Run single scenarios without an account</span>
            </div>
          </div>
          <button className="tier-button guest-button" onClick={() => onClose()}>
            Continue as Guest
          </button>
        </div>

        <div className="tier-card free-tier">
          <div className="tier-header">
            <h3>Free Account</h3>
            <p className="tier-subtitle">Get started for free</p>
          </div>
          <div className="tier-price" style={{color: '#10b981'}}>$0 Forever</div>
          <div className="tier-features">
            <div className="feature">
              <span>Save scenarios</span>
            </div>
            <div className="feature">
              <span>Access from any device</span>
            </div>
            <div className="feature">
              <span>Basic PDF export</span>
            </div>
          </div>
          <button className="tier-button free-button" onClick={() => {
            setWantsSupportersClub(false);
            setActiveTab('register');
          }}>
            Create Free Account
          </button>
        </div>

        <div className="tier-card supporter-tier">
          <div className="tier-header">
            <h3>Supporters Club</h3>
            <p className="tier-subtitle" style={{fontWeight: 'bold'}}>⭐ MOST POPULAR ⭐</p>
          </div>
          <div className="tier-price">$10 per year, no auto-renewal</div>
          <div className="tier-features">
            <div className="feature">
              <span>Unlimited scenarios</span>
            </div>
            <div className="feature">
              <span>PDF export for advisors</span>
            </div>
            <div className="feature">
              <span>Bookkeeping for up to 10 businesses</span>
            </div>
            <div className="feature">
              <span>Scheduling Pro features</span>
            </div>
          </div>
          <button 
            className="tier-button supporter-button" 
            onClick={() => {
              setWantsSupportersClub(true);
              setActiveTab('register');
            }}
          >
            Join Supporters Club
          </button>
        </div>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="auth-form">
      <div className="auth-header">
        <h2>Sign In</h2>
        <p>Welcome back to Larry's Help</p>
      </div>

      <div className="google-register-section">
        <button 
          type="button"
          onClick={handleGoogleLogin} 
          className="google-signin-button"
          disabled={loading}
        >
          {loading ? (
            <span className="loading-spinner">⏳</span>
          ) : (
            <>
              <img 
                src="https://developers.google.com/identity/images/g-logo.png" 
                alt="Google" 
                className="google-icon"
              />
              Sign in with Google
            </>
          )}
        </button>
        
        <div className="divider">
          <span>or sign in with email</span>
        </div>
      </div>

      <form onSubmit={handleLogin}>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" className="auth-button" disabled={loading}>
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </form>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <button 
        type="button" 
        className="google-auth-button" 
        onClick={handleGoogleLogin}
        disabled={loading}
      >
        <div className="google-icon">
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </div>
        {loading ? 'Signing In...' : 'Sign in with Google'}
      </button>

      <div className="auth-footer">
        <p>Don't have an account? <button onClick={() => setActiveTab('register')} className="link-button">Sign Up</button></p>
        <button onClick={() => setActiveTab('sales')} className="link-button">Back to Plans</button>
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="auth-form">
      <div className="auth-header">
        <h2>{wantsSupportersClub ? 'Create Supporters Account' : 'Create Account'}</h2>
        <p>{wantsSupportersClub ? 'Join Larry\'s Help Supporters Club' : 'Join Larry\'s Help'}</p>
      </div>

      <div className="google-register-section">
        <button 
          type="button"
          onClick={handleGoogleRegister} 
          className="google-signin-button"
          disabled={loading}
        >
          {loading ? (
            <span className="loading-spinner">⏳</span>
          ) : (
            <>
              <img 
                src="https://developers.google.com/identity/images/g-logo.png" 
                alt="Google" 
                className="google-icon"
              />
              Continue with Google
            </>
          )}
        </button>
        
        <div className="divider">
          <span>or create with email</span>
        </div>
      </div>

      <form onSubmit={handleRegister}>
        <div className="form-group">
          <label htmlFor="reg-email">Email</label>
          <input
            type="email"
            id="reg-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="reg-password">Password</label>
          <input
            type="password"
            id="reg-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirm-password">Confirm Password</label>
          <input
            type="password"
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        {wantsSupportersClub && (
          <div className="info-message">
            <p>After creating your account, you'll be taken to complete your Supporters Club payment.</p>
          </div>
        )}

        <button type="submit" className="auth-button" disabled={loading}>
          {loading ? (wantsSupportersClub ? 'Creating Account...' : 'Creating Account...') : (wantsSupportersClub ? 'Create Account & Continue to Payment' : 'Create Account')}
        </button>
      </form>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <button 
        type="button" 
        className="google-auth-button" 
        onClick={handleGoogleRegister}
        disabled={loading}
      >
        <div className="google-icon">
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </div>
        {loading ? 'Creating Account...' : 'Sign up with Google'}
      </button>

      <div className="auth-footer">
        <p>Already have an account? <button onClick={() => setActiveTab('login')} className="link-button">Sign In</button></p>
        <button onClick={() => setActiveTab('sales')} className="link-button">Back to Plans</button>
      </div>
    </div>
  );

  return (
    <StripeErrorBoundary><Elements stripe={stripePromise}>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div style={{width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
          <button className="modal-close" onClick={onClose}>×</button>
          </div>
          
          {activeTab === 'sales' && renderSalesPage()}
          {activeTab === 'login' && renderLoginForm()}
          {activeTab === 'register' && renderRegisterForm()}
          {activeTab === 'payment' && (
            <PaymentModal
              isOpen={true}
              onClose={() => setActiveTab('sales')}
              onSuccess={handlePaymentSuccess}
              email={email}
            />
          )}
        </div>
      </div>
    </Elements></StripeErrorBoundary>
  );
};

export default LoginModal; 